import axios from "axios";
import { Message, Loading } from "element-ui"; // 假设使用element-ui做提示
import { throttle } from "./rateLimiters";
import { BASE_URL } from "./requestUrl";
import { getToken, clearToken, clearUserInfo } from "./userInfo";
import router from "@/router"; // 导入路由

// 创建axios实例
const service = axios.create({
  baseURL: BASE_URL, // api的基础url
  timeout: 60000, // 请求超时时间
});

// 使用节流函数包装成功消息的显示
const showSuccessMessage = throttle((message) => {
  Message.success({
    message,
    duration: 1000,
  });
}, 2000);

// 使用节流函数包装成功消息的显示
const showErrorMessage = throttle((message) => {
  Message.error({
    message,
    duration: 1000,
  });
}, 2000);

// 使用节流函数包装警告消息的显示
const showWarningMessage = throttle((message) => {
  Message.warning({
    message,
    duration: 1000,
  });
}, 2000);

// 创建Loading实例的方法
const createLoading = (text = "正在下载文件，请稍候...") => {
  return Loading.service({
    lock: true,
    text: text,
    spinner: "el-icon-loading",
    background: "rgba(0, 0, 0, 0.7)",
  });
};

/**
 * 检查URL是否在白名单中（不需要token的接口）
 * @param {string} url - 请求URL
 * @returns {boolean} - 是否在白名单中
 */
const isUrlInWhiteList = (url) => {
  // 白名单路径列表
  const whiteList = [
    "user/login",
    "user/introduction",
    "user/smsLogin",
    "user/register",
    "user/sendCode",
    "socket/model/**",
  ];

  // 检查URL是否匹配白名单中的任一路径
  return whiteList.some((whiteUrl) => {
    // 对于以 /** 结尾的路径进行特殊处理，匹配前缀
    if (whiteUrl.endsWith("/**")) {
      const prefix = whiteUrl.slice(0, -3); // 去掉 /** 后缀
      return url.startsWith(prefix);
    }
    // 普通路径匹配
    return url.includes(whiteUrl);
  });
};

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 判断是否为白名单接口（无需token的接口）
    const isWhiteListUrl = isUrlInWhiteList(config.url);

    // 获取token
    const token = getToken();

    // 如果不是白名单接口，且没有token，则阻止请求并跳转登录页
    if (!isWhiteListUrl && !token) {
      // 立即中断请求
      config.cancelToken = new axios.CancelToken((cancel) => {
        cancel("未登录，请先登录");
      });

      // 保存当前路径，以便登录后可以返回
      const currentPath = window.location.pathname;
      // 使用 Promise.resolve().then 确保路由跳转在下一个微任务中执行
      Promise.resolve().then(() => {
        router.push({
          path: "/introduction",
          query: { redirect: currentPath },
        }).catch(() => {
          // 忽略导航重复的错误
        });
        showWarningMessage("请先登录");
      });

      return config; // 返回已被取消的配置
    }

    // 有token的情况，添加到请求头
    if (token) {
      config.headers[token["tokenName"]] = `ShanHe ${token["tokenValue"]}`;
    }

    return config;
  },
  (error) => {
    // 处理请求错误
    console.error(error);
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response) => {
    const res = response.data;

    // 这里可以根据后端返回的状态码做不同的处理
    if (res.code !== 200) {
      showWarningMessage(res.message || "错误");

      // 例如：401未授权、403禁止访问等状态码的处理
      if (res.code === 205) {
        clearToken();
        clearUserInfo();
        // 使用 Promise.resolve().then 确保路由跳转在下一个微任务中执行
        Promise.resolve().then(() => {
          router.push({
            path: "/introduction",
            query: { redirect: window.location.pathname }
          }).catch(() => {
            // 忽略导航重复的错误
          });
        });
      }
      console.error(res);
      return Promise.reject(res);
    }
    // 成功
    if (res.code === 200) {
      // showSuccessMessage("成功");
      return res.data;
    }
  },
  (error) => {
    console.error("err:" + error);
    let { message } = error;
    if (error.message == "Network Error") {
      message = "网络错误";
    } else if (error.message == "timeout of 0ms exceeded") {
      message = "请求超时";
    } else if (message == "Request failed with status code 40019") {
      message = "当前人数过多，请排队等候";
    }
    showWarningMessage(message);
    return Promise.reject(error);
  }
);

export default service;

/**
 * 文件下载相关功能
 */

/**
 * 创建用于下载文件的axios实例
 * 与普通请求不同，文件下载需要设置responseType为blob
 */
const downloadService = axios.create({
  baseURL: BASE_URL,
  timeout: 60000, // 下载可能需要更长的超时时间
  responseType: "blob",
});

// 请求拦截器
downloadService.interceptors.request.use(
  (config) => {
    // 获取token
    const token = getToken();

    // 有token的情况，添加到请求头
    if (token) {
      config.headers[token["tokenName"]] = `ShanHe ${token["tokenValue"]}`;
    }

    return config;
  },
  (error) => {
    console.error("下载请求错误:", error);
    return Promise.reject(error);
  }
);

// 响应拦截器
downloadService.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);
function getDownloadHeader(response, headerName) {
  const headers = response && response.headers;
  if (!headers) {
    return "";
  }
  if (typeof headers.get === "function") {
    return headers.get(headerName) || "";
  }
  return headers[String(headerName).toLowerCase()] || headers[headerName] || "";
}

function decodeDownloadFileName(value) {
  const encodedName = String(value || "")
    .trim()
    .replace(/^UTF-8''/i, "")
    .replace(/^['"]|['"]$/g, "");
  try {
    return decodeURIComponent(encodedName);
  } catch (error) {
    console.warn("文件名解码失败", error);
    return encodedName;
  }
}

function getDownloadFileName(disposition) {
  if (!disposition) {
    return "";
  }
  const extendedMatch = /filename\*\s*=\s*([^;]+)/i.exec(disposition);
  if (extendedMatch && extendedMatch[1]) {
    return decodeDownloadFileName(extendedMatch[1]);
  }
  const regularMatch = /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i.exec(disposition);
  return decodeDownloadFileName(regularMatch && (regularMatch[1] || regularMatch[2]));
}

function ensureDownloadExtension(filename, contentType) {
  if (/\.[a-z0-9]{1,10}$/i.test(filename)) {
    return filename;
  }
  const normalizedType = String(contentType || "").toLowerCase();
  if (normalizedType.includes("spreadsheetml.sheet")) {
    return `${filename}.xlsx`;
  }
  if (normalizedType.includes("application/vnd.ms-excel")) {
    return `${filename}.xls`;
  }
  return filename;
}

// 处理文件下载的响应
function handleDownloadResponse(response, customFileName, loadingInstance) {
  let filename = customFileName || "未命名文件";
  const contentType = getDownloadHeader(response, "content-type")
    || response.data?.type
    || "application/octet-stream";

  // 如果没有自定义文件名，尝试从响应头获取
  if (!customFileName) {
    filename = getDownloadFileName(getDownloadHeader(response, "content-disposition")) || filename;
  }
  filename = ensureDownloadExtension(filename, contentType);

  // 处理文件下载
  const blob = new Blob([response.data], { type: contentType });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  URL.revokeObjectURL(link.href);
  document.body.removeChild(link);

  // 关闭加载动画
  if (loadingInstance) {
    loadingInstance.close();
  }

  Message.success({
    message: "文件下载成功",
    duration: 2000,
  });

  return response;
}

// 处理文件下载错误
function handleDownloadError(error, loadingInstance) {
  // 关闭加载动画
  if (loadingInstance) {
    loadingInstance.close();
  }

  console.error("下载响应错误:", error);

  // 解析错误信息
  if (error.response && error.response.data) {
    // 如果服务器返回的是JSON格式的错误信息
    try {
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const errorData = JSON.parse(reader.result);
          Message.error({
            message: errorData.message || "下载失败",
            duration: 3000,
          });
        } catch (e) {
          Message.error({
            message: "下载失败",
            duration: 3000,
          });
        }
      };
      reader.readAsText(error.response.data);
    } catch (e) {
      Message.error({
        message: "下载失败",
        duration: 3000,
      });
    }
  } else {
    Message.error({
      message: error.message || "下载失败",
      duration: 3000,
    });
  }

  return Promise.reject(error);
}

/**
 * GET方式下载文件
 * @param {string} url - 下载地址
 * @param {object} params - 请求参数
 * @param {string} fileName - 自定义下载文件名
 * @param {object} config - 额外配置
 * @param {string} loadingText - 加载提示文本
 * @returns {Promise} - axios请求Promise
 */
export function downloadByGet(
  url,
  params = {},
  fileName = null,
  config = {},
  loadingText
) {
  // 创建加载动画
  const loadingInstance = createLoading(loadingText);

  return downloadService({
    url,
    method: "get",
    params,
    ...config,
  })
    .then((response) =>
      handleDownloadResponse(response, fileName, loadingInstance)
    )
    .catch((error) => handleDownloadError(error, loadingInstance));
}

/**
 * POST方式下载文件
 * @param {string} url - 下载地址
 * @param {object} data - 请求体数据
 * @param {string} fileName - 自定义下载文件名
 * @param {object} config - 额外配置
 * @param {string} loadingText - 加载提示文本
 * @returns {Promise} - axios请求Promise
 */
export function downloadByPost(
  url,
  data = {},
  fileName = null,
  config = {},
  loadingText
) {
  // 创建加载动画
  const loadingInstance = createLoading(loadingText);

  return downloadService({
    url,
    method: "post",
    data,
    ...config,
  })
    .then((response) =>
      handleDownloadResponse(response, fileName, loadingInstance)
    )
    .catch((error) => handleDownloadError(error, loadingInstance));
}

/**
 * 通过文件URL直接下载
 * @param {string} fileUrl - 文件完整URL
 * @param {string} fileName - 下载后的文件名
 * @param {string} loadingText - 加载提示文本
 */
export function downloadByUrl(fileUrl, fileName, loadingText) {
  // 创建加载动画
  const loadingInstance = createLoading(loadingText);

  try {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName || "下载文件";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // 由于直接下载无法确定何时完成，设置一个短暂延迟后关闭loading
    setTimeout(() => {
      loadingInstance.close();
    }, 1000);
  } catch (error) {
    loadingInstance.close();
    Message.error({
      message: "下载失败",
      duration: 3000,
    });
  }
}

/**
 * 下载Blob数据为文件
 * @param {Blob} blob - Blob数据
 * @param {string} fileName - 文件名
 * @param {string} loadingText - 加载提示文本
 */
export function downloadBlob(blob, fileName, loadingText) {
  // 创建加载动画
  const loadingInstance = createLoading(loadingText);

  try {
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName || "下载文件";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(link.href);
    document.body.removeChild(link);

    // 关闭加载动画
    loadingInstance.close();
  } catch (error) {
    loadingInstance.close();
    Message.error({
      message: "下载失败",
      duration: 3000,
    });
  }
}

// 将下载方法添加到service上
service.download = {
  get: downloadByGet,
  post: downloadByPost,
  url: downloadByUrl,
  blob: downloadBlob,
};
